import { useCallback } from 'react'
import { Avatar } from '@mui/material'
import PropTypes from 'prop-types'

import { useAuth } from '@/common/hooks/useAuth'

/**
 * UserAvatar - Displays user initials in an avatar component
 *
 * Uses the unified useAuth() hook to get user information,
 * supporting both MSAL and Kinde authentication providers.
 *
 * @param {Object} props
 * @param {Function} [props.onClick] - Click handler for the avatar
 * @param {Object|Array} [props.sx] - MUI sx prop for styling
 * @returns {JSX.Element}
 */
const UserAvatar = ({ onClick, sx, ...props }) => {
    const { user } = useAuth()
    const { firstName, lastName, name } = user ?? {}

    const getTextAvatar = useCallback(() => {
        if (user) {
            const firstInitial =
                firstName && firstName.length
                    ? firstName.charAt(0).toUpperCase()
                    : name && name.length
                      ? name.charAt(0).toUpperCase()
                      : ''
            const lastInitial = lastName && lastName.length ? lastName.charAt(0).toUpperCase() : ''

            return firstInitial + lastInitial
        }
    }, [firstName, lastName, user, name])

    return (
        <Avatar
            sx={[...(Array.isArray(sx) ? sx : [sx])]}
            onClick={typeof onClick === 'function' ? onClick : undefined}
            {...props}
        >
            {getTextAvatar()}
        </Avatar>
    )
}

UserAvatar.propTypes = {
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
        PropTypes.func,
        PropTypes.object,
    ]),
    onClick: PropTypes.func,
}

export default UserAvatar
